"use client";

import { useEffect, useRef, useState } from "react";

const CATALOG_API_URL = "https://static.14.84.224.46.clients.your-server.de/companion/api/product-catalog";

function catalogUrl(parameters: Record<string, string>) {
  const url = new URL(CATALOG_API_URL);
  for (const [key, value] of Object.entries(parameters)) url.searchParams.set(key, value);
  return url.toString();
}

export type CatalogSuggestion = {
  id: number;
  pharmacy: string;
  ean: string;
  product: string;
  supplier?: string;
  stock: number;
  vat_rate?: number;
  reimbursement_rate?: number;
  product_type?: string;
  purchase_price?: number;
  sale_price?: number;
  market_high?: number;
  market_average?: number;
  market_low?: number;
  market_zone?: number;
};

export function extractGtinFromScan(value: string) {
  const raw = value
    .trim()
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/^\](?:d2|C1|Q3)/i, "")
    .replace(/<GS>|\{GS\}|\[GS\]/gi, "\u001d");
  const parenthesized = raw.match(/\(01\)\s*(\d{14})/);
  if (parenthesized) return parenthesized[1];
  const compact = raw.replace(/[\u001d\u001e\u001f]/g, "|").replace(/[()\s]/g, "");
  const ai01 = compact.match(/(?:^|\|)01(\d{14})/);
  if (ai01) return ai01[1];
  const digits = compact.replace(/\D/g, "");
  if (/^01\d{14}/.test(digits)) return digits.slice(2, 16);
  return /^(?:\d{8}|\d{12,14})$/.test(digits) ? digits : "";
}

export async function resolveCatalogEan(value: string, signal?: AbortSignal) {
  const gtin = extractGtinFromScan(value);
  if (!gtin) return null;
  const candidates = Array.from(new Set([
    gtin,
    gtin.length === 14 && gtin.startsWith("0") ? gtin.slice(1) : "",
    gtin.length === 13 && gtin.startsWith("0") ? gtin.slice(1) : "",
  ].filter(Boolean)));
  for (const normalized of candidates) {
    const response = await fetch(catalogUrl({ ean: normalized, limit: "12" }), {
      cache: "no-store",
      signal,
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error);
    const rows = (payload.items ?? []) as CatalogSuggestion[];
    const exact = rows
      .filter((row) => row.ean === normalized && row.product)
      .sort((a, b) => Number(b.stock) - Number(a.stock))[0] ?? null;
    if (exact) return exact;
  }
  return null;
}

export function CatalogAutocomplete({
  value,
  onChange,
  onPick,
  placeholder,
  inputMode,
  confirmed = false,
  multiple = false,
  selectedEans = [],
  onToggle,
}: {
  value: string;
  onChange: (value: string) => void;
  onPick?: (suggestion: CatalogSuggestion) => void;
  placeholder: string;
  inputMode?: "numeric" | "search" | "text";
  confirmed?: boolean;
  multiple?: boolean;
  selectedEans?: string[];
  onToggle?: (suggestion: CatalogSuggestion) => void;
}) {
  const [suggestions, setSuggestions] = useState<CatalogSuggestion[]>([]);
  const [resolved, setResolved] = useState<CatalogSuggestion | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const onPickRef = useRef(onPick);
  const onToggleRef = useRef(onToggle);
  const lastResolvedRef = useRef("");
  const selectedSuggestionRef = useRef<CatalogSuggestion | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    onPickRef.current = onPick;
  }, [onPick]);

  useEffect(() => {
    onToggleRef.current = onToggle;
  }, [onToggle]);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const query = value.trim().replace(/^\*+/, "*");
    setError("");
    if (confirmed) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    const selected = selectedSuggestionRef.current;
    const normalizedQuery = query.toLocaleLowerCase("fr");
    if (!multiple && selected && (normalizedQuery === selected.product.trim().toLocaleLowerCase("fr") || query === selected.ean)) {
      setSuggestions([]);
      setResolved(selected);
      setOpen(false);
      setLoading(false);
      return;
    }
    const scannedGtin = extractGtinFromScan(query);
    const usefulLength = query.replace(/\*/g, "").length;
    if (usefulLength < 2) {
      setSuggestions([]);
      setResolved(null);
      setOpen(false);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    let requestTimeout = 0;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      requestTimeout = window.setTimeout(() => controller.abort(), 8_000);
      try {
        let nextSuggestions: CatalogSuggestion[] = [];
        if (scannedGtin) {
          const exact = await resolveCatalogEan(query, controller.signal);
          nextSuggestions = exact ? [exact] : [];
        } else {
          const response = await fetch(catalogUrl({ search: query, limit: "12" }), {
            cache: "no-store",
            signal: controller.signal,
          });
          const payload = await response.json();
          if (!response.ok) throw new Error(payload.error);
          const unique = new Map<string, CatalogSuggestion>();
          for (const row of (payload.items ?? []) as CatalogSuggestion[]) {
            const key = `${row.ean}-${row.product}`;
            const current = unique.get(key);
            if (!current || Number(row.stock) > Number(current.stock)) unique.set(key, row);
          }
          nextSuggestions = Array.from(unique.values()).slice(0, 8);
        }
        if (requestIdRef.current !== requestId) return;
        setSuggestions(nextSuggestions);

        const exact = scannedGtin ? nextSuggestions[0] ?? null : null;
        if (exact && !multiple) {
          selectedSuggestionRef.current = exact;
          setResolved(exact);
          setSuggestions([]);
          setOpen(false);
          const resolutionKey = `${exact.ean}:${exact.product}`;
          if (lastResolvedRef.current !== resolutionKey) {
            lastResolvedRef.current = resolutionKey;
            onPickRef.current?.(exact);
          }
        } else {
          setResolved(null);
          setOpen(nextSuggestions.length > 0);
        }
      } catch (error) {
        if (requestIdRef.current !== requestId) return;
        setSuggestions([]);
        setResolved(null);
        setOpen(false);
        setError(controller.signal.aborted
          ? "Recherche trop longue. Réessayez ou saisissez directement l’EAN."
          : "Catalogue momentanément indisponible. Réessayez dans quelques secondes.");
      } finally {
        window.clearTimeout(requestTimeout);
        if (requestIdRef.current === requestId) setLoading(false);
      }
    }, 180);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(requestTimeout);
      controller.abort();
      if (requestIdRef.current === requestId) requestIdRef.current += 1;
    };
  }, [confirmed, multiple, value]);

  return (
    <div className="catalog-autocomplete">
      <input
        value={value}
        inputMode={inputMode}
        onChange={(event) => {
          lastResolvedRef.current = "";
          selectedSuggestionRef.current = null;
          setResolved(null);
          setOpen(false);
          setError("");
          onChange(event.target.value.replace(/^\*+/, "*"));
        }}
        onFocus={() => {
          if (!confirmed && !selectedSuggestionRef.current && suggestions.length > 0) setOpen(true);
        }}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {loading && <span className="catalog-autocomplete-loading">Recherche…</span>}
      {error && <span className="catalog-autocomplete-error" role="status">{error}</span>}
      {resolved && (
        <span className="catalog-autocomplete-resolved">
          <b>✓ {resolved.product}</b>
          <small>EAN {resolved.ean}</small>
        </span>
      )}
      {open && suggestions.length > 0 && (
        <div className={`catalog-autocomplete-results ${multiple ? "multiple" : ""}`}>
          {multiple && <header><span>{selectedEans.length} produit{selectedEans.length > 1 ? "s" : ""} coché{selectedEans.length > 1 ? "s" : ""}</span><button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => suggestions.filter((suggestion) => !selectedEans.includes(suggestion.ean)).forEach((suggestion) => onToggleRef.current?.(suggestion))}>Tout cocher</button></header>}
          {suggestions.map((suggestion) => (
            <button
              type="button"
              className={multiple && selectedEans.includes(suggestion.ean) ? "selected" : ""}
              key={`${suggestion.id}-${suggestion.pharmacy}`}
              aria-pressed={multiple ? selectedEans.includes(suggestion.ean) : undefined}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                if (multiple) {
                  onToggleRef.current?.(suggestion);
                  return;
                }
                lastResolvedRef.current = `${suggestion.ean}:${suggestion.product}`;
                selectedSuggestionRef.current = suggestion;
                setResolved(suggestion);
                setSuggestions([]);
                onPickRef.current?.(suggestion);
                setOpen(false);
              }}
            >
              <span>{multiple && <i>{selectedEans.includes(suggestion.ean) ? "✓" : ""}</i>}<strong>{suggestion.product || suggestion.ean}</strong><small>EAN {suggestion.ean}</small></span>
              <span><b>{suggestion.pharmacy}</b><small>stock {Number(suggestion.stock) || 0}</small></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
